import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTeams } from '../../hooks/useTeams'
import { useMatches } from '../../hooks/useMatches'
import { useTranslation } from 'react-i18next'
import { Button, Badge, DarkModeToggleCompact } from '../../components/ui'
import { Trophy, Settings, Save, Info, Printer } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { BracketGenerator } from '../../components/tools/BracketGenerator'
import { TeamAvatar } from '../../components/ui/TeamAvatar'
import { soundFx } from '../../lib/soundEffects'
import '../../styles/bracket.css'

export const BracketPage = () => {
    const { t } = useTranslation()
    const { tournamentId } = useParams()
    const navigate = useNavigate()
    // Using the Hook's updateScore now for Global Logic!
    const { matches, updateScore, updateMatch, refetch: refetchMatches } = useMatches(tournamentId)
    const { teams } = useTeams(tournamentId)

    const [editingMatch, setEditingMatch] = useState(null)

    // Helper to categorize matches into visual columns
    const getMatchRound = (m) => {
        if (m.type === 'quarter_final') return t('bracket_page.kwartfinale')
        if (m.type === 'semi_final') return t('bracket_page.halve_finale')
        if (m.type === 'final') return t('bracket_page.finale')
        if (m.type === 'third_place') return t('bracket_page.finale') // Show third place in final column for now
        // Legacy fix: map old hardcoded types if present
        if (m.round_name === 'Zestiende Finale' || m.type === 'round_16') return t('bracket_page.zestiende_finale')
        return m.round_name // Fallback for 'knockout' type (Legacy/Random generator)
    }

    // Filter matches for bracket type
    // AND Sort them here to ensure display stability
    const bracketMatches = matches ? matches
        .filter(m => ['knockout', 'quarter_final', 'semi_final', 'final', 'third_place'].includes(m.type))
        .sort((a, b) => {
            // Primary: Sort by match_index (if available) to keep visual order stable
            if ((a.match_index ?? 999) !== (b.match_index ?? 999)) {
                return (a.match_index ?? 999) - (b.match_index ?? 999)
            }
            // Secondary: Sort by round name string definition
            return (a.round_name || '').localeCompare(b.round_name || '', undefined, { numeric: true })
        })
        : []

    const currentTournament = { name: t('bracket_page.default_tournament_name') }

    // --- CHAMPION LOGIC ---
    const finalMatch = bracketMatches.find(m => (m.round_name === 'Finale' || m.type === 'final' || m.round_name === t('bracket_page.finale')) && m.status === 'finished')
    let champion = null
    if (finalMatch) {
        if (parseInt(finalMatch.score_home) > parseInt(finalMatch.score_away)) champion = finalMatch.home_team
        else if (parseInt(finalMatch.score_away) > parseInt(finalMatch.score_home)) champion = finalMatch.away_team
    }

    // Wrapper for update to handle local UI state
    // Now supports Team Updates too!
    const handleUpdateMatch = async (matchId, hScore, aScore, hTeamId, aTeamId) => {
        try {
            // 1. Update Teams first (if changed)
            // We only check if IDs are passed; we assume the caller extracted them correctly
            await updateMatch(matchId, {
                home_team_id: hTeamId,
                away_team_id: aTeamId
            })

            // 2. Update Scores (which triggers promotion logic)
            // Note: updateMatch refreshes data, so updateScore will have fresh team data
            await updateScore(matchId, hScore, aScore)

            setEditingMatch(null)
            // soundFx is handled in hook
        } catch (err) {
            alert(t('common.save_error') + err.message)
            soundFx.play('error')
        }
    }

    return (
        <div className="min-h-screen print:min-h-0 print:bg-white print:m-0 print:p-0">
            <div className="premium-container print:max-w-none print:w-full print:p-0 print:m-0">
                <div className="mb-4 flex justify-between items-center print:hidden">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-900">{t('bracket_page.title')}</h1>
                        <p className="text-gray-500">{t('bracket_page.subtitle')}</p>
                    </div>
                    <DarkModeToggleCompact />
                </div>

                {/* Info Box about Sources */}
                <div className="mb-8 flex items-start gap-3 p-4 bg-blue-50 text-blue-700 rounded-xl border border-blue-100 text-sm print:hidden">
                    <Info className="shrink-0 mt-0.5" size={18} />
                    <div>
                        <strong>{t('bracket_page.how_it_works_title')}</strong><br />
                        {t('bracket_page.how_it_works_text')}{' '}
                        <strong>{t('bracket_page.how_it_works_highlight')}</strong>.
                        <br />
                        <span className="text-blue-500 text-xs mt-1 block">{t('bracket_page.how_it_works_tip')}</span>
                    </div>
                </div>

                {/* CHAMPION BANNER */}
                {champion && (
                    <div className="mb-12 bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 p-1 rounded-2xl shadow-xl animate-fade-in relative overflow-hidden print:shadow-none print:border print:border-yellow-400">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20 print:hidden"></div>
                        <div className="bg-white/90 backdrop-blur-sm rounded-xl p-8 text-center relative z-10">
                            <div className="mx-auto w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mb-4 shadow-inner ring-4 ring-yellow-300">
                                <Trophy size={48} className="text-yellow-600 drop-shadow-sm" />
                            </div>
                            <h2 className="text-xl font-bold text-yellow-800 uppercase tracking-widest mb-1">{t('bracket_page.winner_prefix')} {currentTournament?.name || t('bracket_page.default_tournament_name')}</h2>
                            <h1 className="text-5xl font-black text-gray-900 mb-2">{champion.name}</h1>
                            <p className="text-gray-600 font-medium">{t('bracket_page.congratulations')}</p>
                        </div>
                    </div>
                )}

                {/* ACTION BAR (Reset & Print) */}
                {bracketMatches.length > 0 && (
                    <div className="flex justify-end mb-6 gap-2 print:hidden">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => window.print()}
                            className="mr-auto"
                        >
                            <Printer size={16} className="mr-2" /> Print Schema
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={async () => {
                                if (confirm(t('bracket_page.repair_confirm'))) {
                                    const finishedMatches = bracketMatches.filter(m => m.status === 'finished')
                                    let count = 0

                                    for (const m of finishedMatches) {
                                        // Trigger update (which triggers promotion) by "touching" the match
                                        // We reuse the logic in the hook, but we need access to it.
                                        // Actually, we can just call updateScore with existing values!
                                        try {
                                            await updateScore(m.id, m.score_home, m.score_away)
                                            count++
                                        } catch (e) {
                                            console.error(e)
                                        }
                                    }
                                    alert(t('bracket_page.repair_done', { count }))
                                    refetchMatches()
                                }
                            }}
                        >
                            {t('bracket_page.repair_btn')}
                        </Button>

                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={async () => {
                                if (confirm(t('bracket_page.cleanup_confirm'))) {
                                    const { data: allM } = await supabase.from('matches').select('*').eq('tournament_id', tournamentId).in('type', ['knockout', 'quarter_final', 'semi_final', 'final'])

                                    // Group by round_name
                                    const groups = {}
                                    allM.forEach(m => {
                                        if (!groups[m.round_name]) groups[m.round_name] = []
                                        groups[m.round_name].push(m)
                                    })

                                    const toDelete = []
                                    Object.values(groups).forEach(group => {
                                        if (group.length > 1) {
                                            // Sort: Prefer match with score
                                            group.sort((a, b) => {
                                                const aHasScore = (a.score_home !== null)
                                                const bHasScore = (b.score_home !== null)
                                                if (aHasScore && !bHasScore) return -1 // Keep A
                                                if (!aHasScore && bHasScore) return 1 // Keep B
                                                return b.id - a.id // Keep newest
                                            })

                                            // Delete all except first
                                            for (let i = 1; i < group.length; i++) {
                                                toDelete.push(group[i].id)
                                            }
                                        }
                                    })

                                    if (toDelete.length > 0) {
                                        await supabase.from('matches').delete().in('id', toDelete)
                                        alert(t('bracket_page.cleanup_done', { count: toDelete.length }))
                                        refetchMatches()
                                    } else {
                                        alert(t('bracket_page.cleanup_none'))
                                    }
                                }
                            }}
                        >
                            {t('bracket_page.cleanup_btn')}
                        </Button>

                        <Button
                            variant="warning"
                            size="sm"
                            onClick={async () => {
                                if (confirm(t('common.confirm_wipe_schema'))) {
                                    const { error } = await supabase
                                        .from('matches')
                                        .delete()
                                        .eq('tournament_id', tournamentId)
                                        .in('type', ['knockout', 'quarter_final', 'semi_final', 'final', 'third_place'])

                                    if (!error) {
                                        soundFx.play('pop')
                                        refetchMatches()
                                    }
                                    else alert(t('common.wipe_error') + error.message)
                                }
                            }}
                        >
                            {t('bracket_page.wipe_btn')}
                        </Button>
                    </div>
                )}

                {bracketMatches.length === 0 ? (
                    <div className="max-w-2xl mx-auto mt-12 print:hidden">
                        <BracketGenerator tournamentId={tournamentId} teams={teams} onGenerated={refetchMatches} />
                    </div>
                ) : (
                    <div className="flex flex-col gap-8 overflow-x-auto pb-12 print:overflow-visible print:pb-0">
                        {/* Rounds Loop - DYNAMIC */}
                        {(() => {
                            // Define ordered round types for display
                            const roundOrder = [
                                t('bracket_page.ronde_van', { count: 64 }),
                                t('bracket_page.ronde_van', { count: 32 }),
                                t('bracket_page.zestiende_finale'),
                                t('bracket_page.achtste_finale'),
                                t('bracket_page.kwartfinale'),
                                t('bracket_page.halve_finale'),
                                t('bracket_page.finale')
                            ]

                            // Filter matches that are part of the bracket
                            const relevantMatches = bracketMatches.filter(m => getMatchRound(m) !== 'Unknown')

                            // Get unique rounds present in data
                            const presentRounds = [...new Set(relevantMatches.map(m => getMatchRound(m)))]

                            // Sort them according to our defined order
                            const sortedRounds = presentRounds.sort((a, b) => {
                                return roundOrder.indexOf(a) - roundOrder.indexOf(b)
                            })

                            return sortedRounds.map(roundName => {

                                // Filter matches for this visual round
                                const roundsMatches = bracketMatches.filter(m => getMatchRound(m) === roundName)

                                if (roundsMatches.length === 0) return null

                                return (
                                    <div key={roundName} className="min-w-[350px] max-w-md">
                                        <h3 className="text-lg font-bold text-gray-400 uppercase tracking-widest mb-4 border-b pb-2 flex items-center gap-2">
                                            <Trophy size={16} className="text-gray-300" /> {roundName}
                                        </h3>
                                        <div className="space-y-4">
                                            {roundsMatches.map(match => {
                                                const isEditing = editingMatch === match.id
                                                const hasWinner = match.status === 'finished' && (parseInt(match.score_home) !== parseInt(match.score_away))
                                                const winnerClass = hasWinner ? 'bracket-winner' : ''

                                                return (
                                                    <div key={match.id} className={`bracket-match-card bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${winnerClass} ${isEditing ? 'ring-2 ring-blue-500 shadow-md transform scale-[1.02]' : ''}`}>

                                                        {/* Header */}
                                                        <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-100 h-12">
                                                            <span className="text-xs font-mono text-gray-400">Match #{match.match_index !== null && match.match_index !== undefined ? match.match_index + 1 : ''} {match.round_name}</span>
                                                            {!isEditing ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingMatch(match.id)
                                                                        soundFx.play('click')
                                                                    }}
                                                                    className="p-1.5 hover:bg-white rounded-md text-gray-400 hover:text-blue-500 transition-colors"
                                                                >
                                                                    <Settings size={14} />
                                                                </button>
                                                            ) : (
                                                                <div className="flex gap-1">
                                                                    <button
                                                                        className="p-1.5 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors"
                                                                        onClick={() => {
                                                                            const hScore = document.getElementById(`score-home-${match.id}`).value
                                                                            const aScore = document.getElementById(`score-away-${match.id}`).value
                                                                            const hTeam = document.getElementById(`team-home-${match.id}`).value
                                                                            const aTeam = document.getElementById(`team-away-${match.id}`).value
                                                                            // Convert empty strings to null for teams
                                                                            const cleanHTeam = hTeam === "" ? null : hTeam
                                                                            const cleanATeam = aTeam === "" ? null : aTeam

                                                                            handleUpdateMatch(match.id, hScore, aScore, cleanHTeam, cleanATeam)
                                                                        }}
                                                                        title={t('bracket_page.save_tooltip')}
                                                                    >
                                                                        <Save size={14} />
                                                                    </button>
                                                                    <button
                                                                        className="p-1.5 bg-gray-50 text-gray-400 rounded-md hover:bg-gray-100 hover:text-gray-600 transition-colors"
                                                                        onClick={() => setEditingMatch(null)}
                                                                        title={t('bracket_page.cancel_tooltip')}
                                                                    >
                                                                        <div className="w-3.5 h-3.5 flex items-center justify-center font-bold">✕</div>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Teams & Scores */}
                                                        <div className="p-4 space-y-3">
                                                            {/* Home Team */}
                                                            <div className="flex justify-between items-center">
                                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                    <div className={`rounded-lg shrink-0 ring-2 ${parseInt(match.score_home) > parseInt(match.score_away) && match.status === 'finished' ? 'ring-green-500' : 'ring-transparent'}`}>
                                                                        <TeamAvatar
                                                                            name={match.home_team?.name || '?'}
                                                                            logoUrl={match.home_team?.logo_url}
                                                                            size="sm"
                                                                        />
                                                                    </div>

                                                                    {isEditing ? (
                                                                        <select
                                                                            id={`team-home-${match.id}`}
                                                                            defaultValue={match.home_team_id || ""}
                                                                            className="w-full mr-2 text-sm bg-gray-50 border border-gray-300 rounded px-2 py-1 outline-none focus:border-blue-500"
                                                                        >
                                                                            <option value="">{t('common.unknown')}</option>
                                                                            {teams.map(t => (
                                                                                <option key={t.id} value={t.id}>{t.name}</option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <span className={`font-semibold truncate max-w-[150px] ${parseInt(match.score_home) > parseInt(match.score_away) && match.status === 'finished' ? 'text-green-700' : 'text-gray-700'}`}>
                                                                            {match.home_team?.name || t('common.unknown')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {isEditing ? (
                                                                    <input
                                                                        id={`score-home-${match.id}`}
                                                                        type="number"
                                                                        defaultValue={match.score_home}
                                                                        className="w-12 h-9 text-center bg-blue-50 border border-blue-200 rounded-lg font-bold text-blue-900 focus:ring-2 focus:ring-blue-500 outline-none"
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <span className={`font-bold text-xl w-12 text-center ${parseInt(match.score_home) > parseInt(match.score_away) ? 'text-gray-900' : 'text-gray-400'}`}>{match.score_home ?? '-'}</span>
                                                                )}
                                                            </div>

                                                            {/* Away Team */}
                                                            <div className="flex justify-between items-center">
                                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                    <div className={`rounded-lg shrink-0 ring-2 ${parseInt(match.score_away) > parseInt(match.score_home) && match.status === 'finished' ? 'ring-green-500' : 'ring-transparent'}`}>
                                                                        <TeamAvatar
                                                                            name={match.away_team?.name || '?'}
                                                                            logoUrl={match.away_team?.logo_url}
                                                                            size="sm"
                                                                        />
                                                                    </div>

                                                                    {isEditing ? (
                                                                        <select
                                                                            id={`team-away-${match.id}`}
                                                                            defaultValue={match.away_team_id || ""}
                                                                            className="w-full mr-2 text-sm bg-gray-50 border border-gray-300 rounded px-2 py-1 outline-none focus:border-blue-500"
                                                                        >
                                                                            <option value="">{t('common.unknown')}</option>
                                                                            {teams.map(t => (
                                                                                <option key={t.id} value={t.id}>{t.name}</option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <span className={`font-semibold truncate max-w-[150px] ${parseInt(match.score_away) > parseInt(match.score_home) && match.status === 'finished' ? 'text-green-700' : 'text-gray-700'}`}>
                                                                            {match.away_team?.name || t('common.unknown')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {isEditing ? (
                                                                    <input
                                                                        id={`score-away-${match.id}`}
                                                                        type="number"
                                                                        defaultValue={match.score_away}
                                                                        className="w-12 h-9 text-center bg-blue-50 border border-blue-200 rounded-lg font-bold text-blue-900 focus:ring-2 focus:ring-blue-500 outline-none"
                                                                    />
                                                                ) : (
                                                                    <span className={`font-bold text-xl w-12 text-center ${parseInt(match.score_away) > parseInt(match.score_home) ? 'text-gray-900' : 'text-gray-400'}`}>{match.score_away ?? '-'}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )
                            })
                        })()}
                    </div>
                )}
            </div>
        </div>
    )
}
